import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import Marquee from 'react-fast-marquee';
import { PortfolioDetailsNormalized } from '../Portfolio/PortfolioDetails/PortfolioData';
import SmoothWaveText from '../../Components/SmoothWaveText';
import SliderWrapper from '../../Components/SliderWrapper';

type Img = { src: string; alt: string };

interface PortfolioItem {
  id: string;
  category: string;
  title: string;
  image: Img;
}

const portfolioItems: PortfolioItem[] = PortfolioDetailsNormalized.map(
  (item: any) => ({
    id: item.id,
    category: item.breadcrumb.current,
    title: item.heading,
    image:
      typeof item.mainImage === 'string'
        ? {
            src: item.mainImage as string,
            alt:
              (item.heading || item.breadcrumb?.current || item.id || 'Image') +
              ' – main image',
          }
        : (item.mainImage as Img),
  }),
);

const settings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 700,
  slidesToShow: 2,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  pauseOnHover: false,
  customPaging: () => (
    <Box
      sx={{
        width: 10,
        height: 10,
        backgroundColor: '#000',
        borderRadius: '50%',
        display: 'inline-block',
        mx: 0.2,
      }}
    />
  ),
  appendDots: (dots: any) => (
    <Box
      component="ul"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 3,
        listStyle: 'none',
        '& li.slick-active div': { backgroundColor: '#0f63a5' },
      }}
    >
      {dots}
    </Box>
  ),
  responsive: [{ breakpoint: 900, settings: { slidesToShow: 1 } }],
};

const marqueeWords = Array(10).fill('Exhibits');
const formatUrl = (id: string) => id.toLowerCase().replace(/\s+/g, '-');
const BAR_WIDTH = 30; // px  ← increase/decrease this to change the ribbon width

const PortfolioSection: React.FC = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    gsap.fromTo(
      sectionRef.current,
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 1, ease: 'power3.out' },
    );
  }, []);

  return (
    <Box
      id="portfolio"
      ref={sectionRef}
      sx={{
        py: 5,
        // bgcolor: '#f7f5f0',
        borderTop: '1px solid rgba(15, 99, 165, 0.2)',
        borderBottom: '1px solid rgba(15, 99, 165, 0.2)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background:
            'linear-gradient(180deg, rgba(15, 99, 165, 0.05) 0%, rgba(247, 245, 240, 0) 100%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box>
        <Box sx={{ overflow: 'hidden' }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: 3,
              }}
            >
              <center>
                {' '}
                <SmoothWaveText
                  variant="subtitle2"
                  sx={{
                    color: '#0f63a5',
                    fontFamily: 'GilroyBold, sans-serif',
                    textTransform: 'uppercase',
                    fontSize: '14px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      width: '30%',
                      height: '2px',
                      background: '#0f63a5',
                    },
                  }}
                >
                  Our Works
                </SmoothWaveText>
              </center>
            </Box>
          </Container>

          <Box
            sx={{
              width: '90vw',
              position: 'relative',
              left: '50%',
              right: '50%',
              marginLeft: '-45vw',
              marginRight: '-45vw',
              paddingLeft: '5vw',
              paddingRight: '5vw',
            }}
          >
            <Marquee gradient={true} speed={80} style={{ overflow: 'hidden' }}>
              {marqueeWords.map((word, index) => (
                <Typography
                  key={index}
                  variant="h2"
                  component="span"
                  sx={{
                    fontSize: { xs: '28px', md: '42px' },
                    fontFamily: 'GilroyBold, sans-serif',
                    color: index % 2 === 0 ? '#0f63a5' : 'transparent',
                    WebkitTextStroke: index % 2 === 0 ? '0px' : '1px #0f63a5',
                    textTransform: 'uppercase',
                    mx: 3,
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                    opacity: 0.9,
                  }}
                >
                  {word}
                </Typography>
              ))}
            </Marquee>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl">
        <Box sx={{ py: 6, position: 'relative',px:2 }}>
          <SliderWrapper {...settings}>
            {portfolioItems.map((item, index) => (
              <Box key={index} sx={{ }}>
                <Box
                  onClick={() => navigate(`/portfolio/${formatUrl(item.id)}`)}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    height: 550,
                    transition: 'all 0.5s ease',
                    transform: 'scaleX(1)',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 45px rgba(15, 99, 165, 0.2)',
                    },
                    '&:hover .overlay': {
                      opacity: 1,
                      visibility: 'visible',
                      background:
                        'linear-gradient(to bottom, rgba(15, 99, 165, 0.9), rgba(0, 0, 0, 0.8))',
                    },
                  }}
                >
                  {/* IMAGE WRAPPER */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {/* fixed-width blue ribbon with HORIZONTAL label (rotated -90°) */}
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${BAR_WIDTH}px`,
                        height: '100%',
                        backgroundColor: '#0f63a5',
                        zIndex: 9,
                        overflow: 'hidden',
                      }}
                    >
                      {/* center the text then rotate so it reads horizontally along the ribbon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) rotate(-90deg)',
                          transformOrigin: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#fff',
                            fontWeight: 800,
                            letterSpacing: '0.12em',
                            lineHeight: 1,
                            p: 0,
                            m: 0,
                            display: 'inline-block',
                            fontSize: { xs: 11, md: 12 },
                            userSelect: 'none',
                          }}
                        >
                          {item.category}
                        </Typography>
                      </Box>
                    </Box>

                    {/* IMAGE */}
                    <img
                      src={item.image.src}
                      srcSet={`${item.image.src}?w=360 360w, ${item.image.src}?w=640 640w, ${item.image.src}?w=960 960w, ${item.image.src}?w=1200 1200w`}
                      sizes="(max-width: 900px) 90vw, 50vw"
                      alt={
                        item.image.alt || item.title || item.category || item.id
                      }
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />

                    {/* Overlay */}
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        padding: '2rem',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          mb: 1,
                          transform: 'translateY(20px)',
                          opacity: 0,
                          transition: 'all 0.4s ease',
                          '.overlay:hover &': {
                            transform: 'translateY(0)',
                            opacity: 1,
                          },
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          transform: 'translateY(20px)',
                          opacity: 0,
                          transition: 'all 0.4s ease 0.1s',
                          '.overlay:hover &': {
                            transform: 'translateY(0)',
                            opacity: 1,
                          },
                        }}
                      >
                        Explore Project
                        <Box
                          component="span"
                          sx={{
                            width: 30,
                            height: 1,
                            bgcolor: '#fff',
                            display: 'inline-block',
                            transition: 'width 0.3s ease',
                            '.overlay:hover &': {
                              width: 50,
                            },
                          }}
                        />
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </SliderWrapper>
        </Box>
      </Container>
    </Box>
  );
};

export default PortfolioSection;
